import React, { useEffect, useState } from 'react';
import { Modal, Button, Form, ListGroup, Badge } from "react-bootstrap";
import { useLoader } from '../../_helper/LoaderContext';
import { reportList, sendEmail, sendWhatsApp } from './service/report-test-service';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import S3ImageLoader from '../../S3ImageLoader';
import FileLink from '../../FileLink';
import { useLicense } from '../../_helper/LicenseContext';
import PrintReport from './PrintReport';
import { Printer } from 'lucide-react';

export default function ReportList({ setUploadModelOpen, toggleUploadModel, data }) {
    const { setLoading } = useLoader();
    const [reports, setReports] = useState([]);
    const [selectedReports, setSelectedReports] = useState([]);
    const [selectAll, setSelectAll] = useState(false);
    const { decodeTokenOnly } = useLicense()
    const [whastappAllow, setWhatsappAllow] = useState(false)
    const [emailAllow, setEmailAllow] = useState(false);
    const [print, setPrint] = useState(false);
    const [imageUrl, setImageUrl] = useState("")

    useEffect(() => {
        decodeTokenOnly()
            .then((decoded) => {
                // console.log(decoded)
                if (decoded.features.whatsapp_reports == true) {
                    setWhatsappAllow(true);
                } else {
                    setWhatsappAllow();
                }
                if (decoded.features.email_reports == true) {
                    setEmailAllow(true);
                } else {
                    setEmailAllow();
                }
            })
            .catch(() => {
                setWhatsappAllow();
                setEmailAllow()
            });
    }, [])

    useEffect(() => {
        if (data?._id) {
            getReportList();
        }
    }, [data]);

    const getReportList = async () => {
        setLoading(true);
        try {
            const payload = { testOrderId: data._id };
            const res = await reportList(payload);
            // console.log(res)
            if (res.data.success == true) {
                setReports(res.data.data || []);
            } else {
                setReports([]);
            }
        } catch (error) {
            console.error("Failed to load reports:", error);
            setReports([]);
        } finally {
            setLoading(false);
        }
    };

    // Toggle single checkbox
    const handleCheckboxChange = (reportId) => {
        setSelectedReports(prev =>
            prev.includes(reportId)
                ? prev.filter(id => id !== reportId)
                : [...prev, reportId]
        );
    };

    // Toggle Select All
    const handleSelectAll = () => {
        if (selectAll) {
            setSelectedReports([]);
        } else {
            setSelectedReports(reports.map(r => r._id));
        }
        setSelectAll(!selectAll);
    };

    // Send Button - Console log selected IDs
    const handleSend = async () => {
        if (selectedReports.length === 0) {
            alert("Please select at least one report!");
            return;
        }
        const payload = {
            testReportIds: selectedReports
        }
        setLoading(true)
        try {
            const res = await sendEmail(payload);
            if (res.data.success == true) {
                toast.success(res.data.message);
                toggleUploadModel()
            }
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false)
        }
    };

    const handleWhatsapp = async () => {
        if (selectedReports.length === 0) {
            alert("Please select at least one report!");
            return;
        }
        const payload = {
            testReportIds: selectedReports,
            message: "Lab Test Report"
        }
        setLoading(true)
        try {
            const res = await sendWhatsApp(payload);
            if (res.data.success == true) {
                toast.success(res.data.message);
                toggleUploadModel()
            }
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false)
        }
    }

    const openModelForPrint = (image) => {
        setPrint(true)
        setImageUrl(image);
        console.log(image)
    }

    return (
        <>
            <Modal.Header closeButton>
                <Modal.Title>Report List</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {/* Body */}
                <div className="tw-flex-1 tw-overflow-y-auto">
                    {reports.length === 0 ? (
                        <div className="tw-text-center tw-py-16">
                            <div className="tw-bg-gray-200 tw-border-2 tw-border-dashed tw-rounded-xl tw-w-24 tw-h-24 tw-mx-auto tw-mb-4 tw-flex tw-items-center tw-justify-center">
                                <i className="fa fa-file-pdf-o tw-text-4xl tw-text-gray-400"></i>
                            </div>
                            <p className="tw-text-gray-500 tw-text-lg">No reports generated yet</p>
                        </div>
                    ) : (
                        <>
                            {/* Select All */}
                            <div className="tw-flex tw-items-center tw-justify-between tw-mb-5 tw-p-4 tw-bg-gray-50 tw-rounded-xl">
                                <label className="tw-flex tw-items-center tw-gap-3 tw-cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={selectAll}
                                        onChange={handleSelectAll}
                                        className="tw-w-6 tw-h-6 tw-rounded tw-border-2 tw-border-gray-300 
                                                   checked:tw-bg-[#025720] checked:tw-border-[#025720] 
                                                   focus:tw-ring-4 focus:tw-ring-[#025720]/30 
                                                   focus:tw-outline-none tw-transition-all tw-duration-200"
                                    />
                                    <span className="tw-font-semibold tw-text-gray-700">Select All</span>
                                </label>
                                <span className="tw-text-sm tw-text-gray-600">
                                    {selectedReports.length} of {reports.length} selected
                                </span>
                            </div>

                            {/* Reports List */}
                            <div className="tw-space-y-4">
                                {reports.map((report) => {
                                    const isSelected = selectedReports.includes(report._id);

                                    return (
                                        <div
                                            key={report._id}
                                            className={`tw-border-2 tw-rounded-xl tw-p-3 tw-transition-all tw-duration-200 ${isSelected
                                                ? "tw-border-[#025720] tw-bg-green-50 tw-shadow-lg"
                                                : "tw-border-gray-200 hover:tw-border-gray-300"
                                                }`}
                                        >
                                            <div className="tw-flex tw-items-start tw-gap-4">
                                                {/* Custom Tailwind Checkbox */}
                                                <label className="tw-flex tw-items-center tw-mt-1 tw-cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        onChange={() => handleCheckboxChange(report._id)}
                                                        className="tw-w-6 tw-h-6 tw-rounded tw-border-2 tw-border-gray-300 
                                                                   checked:tw-bg-[#025720] checked:tw-border-[#025720] 
                                                                   focus:tw-ring-4 focus:tw-ring-[#025720]/30 
                                                                   focus:tw-outline-none tw-transition-all tw-duration-200"
                                                    />
                                                </label>

                                                <div className="tw-flex-1">
                                                    <h4 className="tw-font-bold tw-text-lg tw-text-gray-800">
                                                        {report.testname || report.reportKey}
                                                    </h4>
                                                    <div className="tw-text-sm tw-text-gray-600 tw-space-y-1 tw-mt-1">
                                                        <p className='mb-1'>
                                                            <span className="tw-font-medium">Report ID:</span>{' '}
                                                            <code className="tw-bg-gray-200 tw-px-2 tw-py-1 tw-rounded tw-text-xs">
                                                                {report.reportId}
                                                            </code>
                                                        </p>
                                                        <p className='mb-1'>
                                                            <span className="tw-font-medium">Generated:</span>{' '}
                                                            {format(new Date(report.createdAt), "dd MMM yyyy, hh:mm a")}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="">
                                                    <div className="tw-text-right mb-2">
                                                        {/* {report.reportPdfPath && ( */}
                                                        <FileLink
                                                            fileName={report?.reportPdfPath}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            style={{ textDecoration: "none" }}
                                                            className='tw-text-black hover:tw-text-white tw-rounded-xl px-2 py-1 hover:tw-bg-[#1b255b] '
                                                        >
                                                            View Report
                                                        </FileLink>
                                                        {/* )} */}

                                                    </div>
                                                    <span onClick={() => openModelForPrint(report?.reportPdfPath)} className='tw-text-black hover:tw-text-white tw-rounded-xl px-2 py-1 hover:tw-bg-[#1b255b] tw-cursor-pointer'
                                                    style={{border:'1px solid #1b255b'}}>
                                                        <Printer className='tw-w-4 tw-h-4' /> Print Report</span >
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    )}
                </div>

            </Modal.Body>

            <Modal.Footer style={{ display: "block" }} className='text-center'>
                <div className="text-center mb-3">
                    <div className="tw-text-xl tw-font-bold">Send To</div>
                </div>
                <div className="tw-flex tw-justify-center">
                    {whastappAllow == true && (
                        <div className="text-success tw-cursor-pointer text-center me-3" onClick={handleWhatsapp} style={{ lineHeight: '0px' }}>
                            <i class="bi bi-whatsapp tw-text-[35px]"></i>
                            <div className="tw-text-lg tw-font-bold">WhatsApp</div>
                        </div>
                    )}
                    {emailAllow == true && (
                        <div className="text-center tw-text-cyan-500 tw-cursor-pointer" onClick={handleSend} style={{ lineHeight: '0px' }}>
                            <i class="bi bi-envelope-arrow-up-fill tw-text-[35px]"></i>
                            <div className="tw-text-lg tw-font-bold">Email</div>
                        </div>
                    )}
                </div>
            </Modal.Footer>

            <PrintReport print={print} setPrint={setPrint} imageUrl={imageUrl} />
        </>
    );
}